import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { movementConceptsApi, movementsApi } from "./api";

export const movementKeys = {
  all: ["movements"] as const,
  concepts: () => [...movementKeys.all, "concepts"] as const,
  conceptList: (type?: "income" | "expense") => [...movementKeys.concepts(), type ?? "all"] as const,
  recent: (type?: "income" | "expense") => [...movementKeys.all, "recent", type ?? "all"] as const,
  lists: () => [...movementKeys.all, "list"] as const,
  list: (params: Parameters<typeof movementsApi.list>[0]) => [...movementKeys.lists(), params] as const,
  details: () => [...movementKeys.all, "detail"] as const,
  detail: (id: number) => [...movementKeys.details(), id] as const,
};

export function useMovementConcepts(type?: "income" | "expense") {
  return useQuery({
    queryKey: movementKeys.conceptList(type),
    queryFn: () => movementConceptsApi.list({ type }),
  });
}

export function useRecentConcepts(type?: "income" | "expense", limit = 5) {
  return useQuery({
    queryKey: [...movementKeys.recent(type), limit],
    queryFn: () => movementsApi.recentConcepts({ type, limit }),
  });
}

export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof movementsApi.create>[0]) => movementsApi.create(body),
    onSuccess: () => {
      // After a new movement, the daily summary changes, and the
      // recent-concepts list reorders.
      qc.invalidateQueries({ queryKey: movementKeys.all });
      qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });
}

export function useCreateMovementConcept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof movementConceptsApi.create>[0]) =>
      movementConceptsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: movementKeys.concepts() });
    },
  });
}

export function useMovements(
  params: Parameters<typeof movementsApi.list>[0] = {},
  enabled = true
) {
  return useQuery({
    queryKey: movementKeys.list(params),
    queryFn: () => movementsApi.list(params),
    enabled,
  });
}

export function useMovement(id: number | null, enabled = true) {
  return useQuery({
    queryKey: movementKeys.detail(id ?? -1),
    queryFn: () => movementsApi.get(id!),
    enabled: enabled && id != null,
  });
}

function invalidateFinancialViews(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: movementKeys.all });
  qc.invalidateQueries({ queryKey: ["summary"] });
}

export function useUpdateMovementNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof movementsApi.updateNote>[1] }) =>
      movementsApi.updateNote(id, body),
    onSuccess: (movement) => {
      qc.setQueryData(movementKeys.detail(movement.id), movement);
      invalidateFinancialViews(qc);
    },
  });
}

export function useVoidMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof movementsApi.void>[1] }) =>
      movementsApi.void(id, body),
    onSuccess: (movement) => {
      qc.setQueryData(movementKeys.detail(movement.id), movement);
      invalidateFinancialViews(qc);
    },
  });
}

export function useCorrectMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof movementsApi.correct>[1] }) =>
      movementsApi.correct(id, body),
    onSuccess: () => invalidateFinancialViews(qc),
  });
}

export function useReverseMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Parameters<typeof movementsApi.reverse>[1] }) =>
      movementsApi.reverse(id, body),
    onSuccess: () => invalidateFinancialViews(qc),
  });
}
